/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
// TODO: this can be tree shakable if we only use the ones we need (build with all existing for now)
// TODO: eventually we should switch to 'files' in svg-to-ts, which will be tree-shakable AND lazy load friendly
import * as icons from './falcon-icons';
import { FalconIcon } from './falcon-icons';

/**
 * Shareable service that can add custom icons to the matIconRegistry. Makes a consistent
 * means on adding on new icons, either by default, here in ng-falcon, or ad-hoc in each application.
 */
@Injectable({
  providedIn: 'root'
})
export class IconService {

  private _initialized = false;

  // hold a cache of named icons, and reference
  // this when registering new ones to prevent duplicate
  // references - Set<namespace:identifier>
  private _registered: Set<string> = new Set();

  get initialized() {
    return this._initialized;
  }

  constructor(private iconRegistry: MatIconRegistry, private domSanitizer: DomSanitizer) {}

  // register all existing shared icons
  init() {
    Object.values(icons).forEach((icon: FalconIcon) => {
      this.registerViaSvg(`ngf`, `${icon.name}`, icon.data);
    });

    this._initialized = true;
  }

  /**
   * @param namespace the namespace the icon exists within
   * @param identifier the name by which the icon can be referenced in the template
   * (e.g. 'some-icon',  <mat-icon svgIcon="namespace:some-icon"></mat-icon>)
   * @param svgUrl the url to load the svg from (should be relative to server for app)
   * 
   */
  registerViaUrl(namespace: string, identifier: string, svgUrl: string) {

    if (!namespace) {
      throw Error(`namespace must be defined to register icon ${identifier}`);
    }

    if(!this._registered.has(`${namespace}:${identifier}`)) {
      this._registered.add(`${namespace}:${identifier}`);
      this.iconRegistry.addSvgIconInNamespace(namespace, identifier, this.domSanitizer.bypassSecurityTrustResourceUrl(svgUrl));
    }
  }

  /**
   * @param namespace the namespace the icon exists within
   * @param identifier the name by which the icon can be referenced in the template
   * (e.g. 'some-icon',  <mat-icon svgIcon="some-icon"></mat-icon>)
   * @param svg the svg string literal to use for the icon
   */
  registerViaSvg(namespace: string, identifier: string, svg: string) {

    if (!namespace) {
      throw Error(`namespace must be defined to register icon ${identifier}`);
    }

    if(!this._registered.has(`${namespace}:${identifier}`)) {
      this._registered.add(`${namespace}:${identifier}`);
      this.iconRegistry.addSvgIconLiteralInNamespace(namespace, identifier, this.domSanitizer.bypassSecurityTrustHtml(svg));
    }

  }

  registerSvgIcon(namespace: string, icon: {name: string, data: string}) {
    this.registerViaSvg(namespace, icon.name, icon.data);
  }

  registerSvgIcons(namespace: string, icons: {name: string, data: string}[]) {
    icons.forEach(icon => {
      this.registerViaSvg(namespace, icon.name, icon.data);
    })
  }

  /**
   *
   * @param toRegister list of icons ({name, type}) to try registering
   * @param baseUrl shared base url for svg path
   */
  registerIconsViaUrl(toRegister: {name: string; type: 'ngf' | 'mat' | 'svg' | string; }[], baseUrl: string) {

    if (!baseUrl) {
      throw Error('must provide a baseUrl to register icons');
    }

    // no need to include mat/ngf icons
    // should already be loaded to the registry
    toRegister = toRegister.filter(r => r.type !== 'ngf' && r.type !== 'mat' && r.type !== 'svg');

      if (toRegister.length) {
        toRegister.forEach(icon => {
          // register all the icons via the provided base url
          this.registerViaUrl(icon.type, icon.name, `${baseUrl}/${icon.name}.svg`);
        });
      }
  }
}
